import React from 'react';

const TicketHossTermsContent = () => (
  <>
    <h1>TICKET HOSS TERMS OF SERVICE</h1>
    <p className="last-updated">Last Updated: January 12, 2026</p>

    <p>
      Ticket Hoss is owned and operated by Pit Pay, Inc., a Delaware
      corporation.
    </p>
    <p>
      These Terms of Service ("<strong>Terms</strong>" or "
      <strong>Agreement</strong>") govern Promoter's use of the Ticket Hoss
      platform and services. By using the Platform, Promoter agrees to be bound
      by these Terms.
    </p>

    <h3>1. DEFINITIONS</h3>
    <p>
      "<strong>Event</strong>" means any event operated by Promoter for which
      Tickets are sold through the Platform.
    </p>
    <p>
      "<strong>Ticket Holder</strong>" means any individual who purchases a
      Ticket or item through the Platform.
    </p>
    <p>
      "<strong>Ticket</strong>" means any ticket, credential, or other item sold
      by Promoter through the Platform.
    </p>
    <p>
      "<strong>Platform</strong>" means the Ticket Hoss mobile application,
      website, and related software and services.
    </p>
    <p>
      "<strong>Promoter Content</strong>" means all content, information,
      images, and materials provided by Promoter for use on the Platform.
    </p>
    <p>
      "<strong>Representatives</strong>" means Promoter's employees, agents,
      contractors, and other authorized personnel.
    </p>
    <p>
      "<strong>Transaction Data</strong>" means all data generated through
      Promoter's use of the Platform, including Ticket Holder information,
      purchase records, and Event attendance data.
    </p>

    <h3>2. SOFTWARE LICENSE AND PERMITTED USE</h3>
    <h4>2.1 License Grant</h4>
    <p>
      Pit Pay grants Promoter a limited, non-exclusive, non-transferable license
      to download and use the Platform solely for internal business purposes
      related to Events.
    </p>
    <h4>2.2 Proprietary Rights</h4>
    <p>
      Pit Pay owns all right, title, and interest in and to the Platform,
      including any enhancements or modifications developed based on ideas or
      feedback communicated by Promoter.
    </p>
    <h4>2.3 Restrictions</h4>
    <p>
      Promoter shall not (and shall ensure Representatives do not) copy, modify,
      reverse engineer, decompile, or attempt to gain access to the source code
      of the Platform.
    </p>

    <h3>3. PROMOTER RESPONSIBILITIES</h3>
    <h4>3.1 Legal Compliance</h4>
    <p>
      Promoter must comply with all applicable federal, state, and local laws,
      rules, and regulations and secure all necessary licenses and permissions
      for Promoter Content and the operation of Events.
    </p>
    <h4>3.2 On-Site Operations</h4>
    <p className="indent">
      <strong>(a) Equipment.</strong> Promoter is responsible for procuring,
      maintaining, and operating all equipment necessary for Event check-in and
      operations, including mobile devices, tablets, scanners, kiosks, wristband
      printers, computers, and internet connectivity. Pit Pay does not provide
      hardware.
    </p>
    <p className="indent">
      <strong>(b) Check-In Process.</strong> Promoter is responsible for
      establishing and executing the check-in process at Events, including
      verifying Ticket Holder credentials, issuing wristbands or other physical
      credentials, and ensuring only authorized Ticket Holders gain access.
    </p>
    <p className="indent">
      <strong>(c) Designated Location.</strong> Promoter must provide an
      expedited experience for Ticket Hoss users, including a designated window
      or pick-up location with appropriate signage.
    </p>
    <h4>3.3 Training</h4>
    <p>
      Promoter is responsible for ensuring all Representatives understand how
      the Platform works end-to-end and how to properly execute on-site
      processes. Pit Pay provides training resources, but Promoter is solely
      responsible for ensuring staff competency.
    </p>
    <h4>3.4 Capacity and Reserved Inventory</h4>
    <p className="indent">
      <strong>(a) Accurate Information.</strong> Promoter is solely responsible
      for providing accurate capacity limits, maps, seating charts, and
      inventory information for all Events.
    </p>
    <p className="indent">
      <strong>(b) Map and Configuration Verification.</strong> Promoter must
      review and verify all maps, seating charts, and configurations BEFORE
      sales begin. Pit Pay is not responsible for errors resulting from
      inaccurate or incomplete information.
    </p>
    <p className="indent">
      <strong>(c) Double Booking.</strong> Pit Pay shall have no liability for
      any double booking of reserved items resulting from inaccurate inventory
      information provided by Promoter.
    </p>
    <p className="indent">
      <strong>(d) Overselling.</strong> Promoter is solely responsible for any
      overselling of capacity resulting from inaccurate information. Pit Pay
      shall have no liability for any claims arising from Ticket Holders denied
      entry due to capacity issues.
    </p>
    <h4>3.5 Account Access and User Management</h4>
    <p className="indent">
      <strong>(a) User Accounts.</strong> Promoter may create multiple user
      accounts with varying permission levels. Promoter is solely responsible
      for managing user accounts.
    </p>
    <p className="indent">
      <strong>(b) Credential Security.</strong> Promoter is responsible for
      maintaining the security of all login credentials and ensuring only
      authorized individuals have access.
    </p>
    <p className="indent">
      <strong>(c) Responsibility for Users.</strong> Promoter is responsible for
      all actions taken by users with access to Promoter's account, whether
      authorized or unauthorized.
    </p>

    <h3>4. INSURANCE</h3>
    <h4>4.1 Coverage Required</h4>
    <p>
      Promoter must maintain commercial general liability insurance covering
      bodily injury and property damage arising from Events. Promoter shall name
      Pit Pay, Inc. and its affiliates as additional insureds on such policy.
    </p>
    <h4>4.2 Proof of Insurance</h4>
    <p>
      Promoter shall provide a certificate of insurance upon request by Pit Pay.
    </p>
    <h4>4.3 Recommended Additional Coverage</h4>
    <p>
      Pit Pay strongly recommends Promoter maintain the following additional
      coverage:
    </p>
    <p className="indent-more">(i) Property insurance covering facility, equipment, and assets</p>
    <p className="indent-more">
      (ii) Cyber liability insurance covering data breaches and privacy claims
    </p>

    <h3>5. PAYMENT PROCESSING AND FEES</h3>
    <h4>5.1 Payment Processing Structure</h4>
    <p className="indent">
      <strong>(a) Third-Party Processor.</strong> Pit Pay utilizes Stripe, Inc.
      and its Stripe Connect platform ("<strong>Payment Processor</strong>") to
      facilitate payment processing and fund distribution.
    </p>
    <p className="indent">
      <strong>(b) Marketplace Model.</strong> The Platform operates as a
      marketplace whereby Ticket Holders purchase Tickets from Promoter, with
      payments processed through Pit Pay's Payment Processor infrastructure.
    </p>
    <p className="indent">
      <strong>(c) Merchant of Record.</strong> Promoter acknowledges that Pit
      Pay serves as the merchant of record for transactions processed through
      the Platform.
    </p>
    <p className="indent">
      <strong>(d) Payment Processor Terms.</strong> Promoter agrees to be bound
      by Payment Processor terms as a condition of using the Platform.
    </p>
    <h4>5.2 Fees and Payouts</h4>
    <p className="indent">
      <strong>(a) Transaction Fees.</strong> Pit Pay charges Transaction Fees as
      outlined in Exhibit A (Fee Schedule). These fees are non-refundable.
    </p>
    <p className="indent">
      <strong>(b) Remittance.</strong> Pit Pay will remit 100% of the price set
      by Promoter, less applicable Transaction Fees.
    </p>
    <p className="indent">
      <strong>(c) Payout Timing.</strong> Pit Pay holds funds until after the
      Event date. ACH payouts typically occur approximately four (4) business
      days after the Event.
    </p>
    <p className="indent">
      <strong>(d) Payout Withholding.</strong> Pit Pay may withhold or delay
      payouts until all pending refund, credit, or Buyer Protection requests for
      an Event are resolved and settled.
    </p>
    <h4>5.3 Payment Method on File</h4>
    <p className="indent">
      <strong>(a) Requirement.</strong> Promoter must maintain a valid payment
      method on file at all times.
    </p>
    <p className="indent">
      <strong>(b) Authorization to Charge.</strong> Promoter authorizes Pit Pay
      to charge such payment method for chargebacks, account shortfalls, fees,
      and any other amounts owed.
    </p>
    <p className="indent">
      <strong>(c) Failed Charges.</strong> If a charge fails, Promoter shall
      provide an updated payment method within five (5) business days.
    </p>
    <h4>5.4 Chargebacks and Disputes</h4>
    <p className="indent">
      <strong>(a) Promoter Responsibility.</strong> Promoter is solely
      responsible for all chargebacks, disputes, and fraud claims, including the
      disputed amount and any fees (currently $15 per dispute).
    </p>
    <p className="indent">
      <strong>(b) Cooperation.</strong> Promoter shall cooperate with Pit Pay in
      responding to chargebacks.
    </p>
    <h4>5.5 Taxes</h4>
    <p>
      Promoter is responsible for collecting and paying all applicable taxes
      arising from the sale of Tickets (other than taxes on Pit Pay's income).
    </p>
    <h4>5.6 1% Cash Back Program</h4>
    <p className="indent">
      <strong>(a) Availability.</strong> Where Participant-Paid fee structures
      are in effect, a 1% Cash Back contribution shall be calculated on
      processing volume.
    </p>
    <p className="indent">
      <strong>(b) Annual Payout.</strong> Pit Pay shall pay the accumulated
      balance annually following the calendar year end.
    </p>
    <p className="indent">
      <strong>(c) Right to Withhold.</strong> Pit Pay may offset amounts owed
      against the Cash Back balance.
    </p>
    <p className="indent">
      <strong>(d) Inapplicability.</strong> Does not apply where all-in pricing
      is required or elected.
    </p>

    <h3>6. POINT OF SALE SYSTEM</h3>
    <p className="indent">
      <strong>(a) Availability.</strong> Pit Pay may provide a Point of Sale
      (POS) system for walk-up and on-site sales.
    </p>
    <p className="indent">
      <strong>(b) Equipment.</strong> Promoter is responsible for purchasing and
      maintaining all POS hardware including tablets, receipt printers,
      wristband printers, and card readers. Pit Pay does not provide hardware.
    </p>
    <p className="indent">
      <strong>(c) All-In Pricing.</strong> Walk-up POS sales use all-in pricing
      only. Fees are deducted from Promoter's payout.
    </p>
    <p className="indent">
      <strong>(d) Training.</strong> Pit Pay provides training resources.
      Promoter is responsible for ensuring staff are properly trained on POS
      operations.
    </p>
    <p className="indent">
      <strong>(e) Internet Connectivity.</strong> Promoter is responsible for
      providing reliable internet connectivity at POS locations.
    </p>

    <h3>7. REFUNDS, CANCELLATIONS, AND CREDITS</h3>
    <h4>7.1 Refund Policy</h4>
    <p className="indent">
      <strong>(a) Default Policy.</strong> Unless otherwise specified, all sales
      are non-refundable. Promoter may establish a more favorable refund policy.
    </p>
    <p className="indent">
      <strong>(b) Refund Policy Disclosure.</strong> Promoter must clearly
      display its refund policy on Event listings.
    </p>
    <p className="indent">
      <strong>(c) Transaction Fees on Refunds.</strong> All fees associated with
      the original transaction are non-refundable to Promoter.
    </p>
    <h4>7.2 Event Cancellation</h4>
    <p className="indent">
      <strong>(a) Full Cancellation.</strong> If an Event is cancelled, Promoter
      shall issue refunds or credits to all Ticket Holders.
    </p>
    <p className="indent">
      <strong>(b) Postponement.</strong> Promoter may transfer purchases to
      rescheduled Events but should offer refunds to those who cannot attend.
    </p>
    <p className="indent">
      <strong>(c) Partial Completion.</strong> Refund decisions for partially
      completed Events are at Promoter's discretion, subject to applicable law.
    </p>
    <h4>7.3 Credits</h4>
    <p className="indent">
      <strong>(a) Credit Issuance.</strong> Promoter may issue Platform credits
      in lieu of refunds, subject to applicable law.
    </p>
    <p className="indent">
      <strong>(b) Credit Compliance.</strong> Promoter is responsible for
      complying with all laws governing credits and stored value.
    </p>
    <p className="indent">
      <strong>(c) Credit Obligations Survive Termination.</strong> Promoter
      remains responsible for honoring credits after termination.
    </p>
    <h4>7.4 Buyer Protection Program</h4>
    <p className="indent">
      <strong>(a) Program Options.</strong> Promoter may opt to offer Ticket
      Holders one or both of the following Buyer Protection options at checkout:
      (i) Credit for Any Reason - Ticket Holder receives Platform credit if
      unable to attend; (ii) Refund for Any Reason - Ticket Holder receives a
      refund if unable to attend.
    </p>
    <p className="indent">
      <strong>(b) Opt-In.</strong> Promoter's election to offer Buyer Protection
      programs is made through Platform settings.
    </p>
    <p className="indent">
      <strong>(c) Binding Commitment.</strong> If Promoter opts into a Buyer
      Protection program, Promoter must honor all requests from Ticket Holders.
      Requests are processed automatically without requiring justification.
    </p>
    <p className="indent">
      <strong>(d) No Questions Asked.</strong> Buyer Protection programs are
      marketed as providing Ticket Holders peace of mind and flexibility.
      Promoter may not contest, deny, or circumvent legitimate Buyer Protection
      requests.
    </p>
    <p className="indent">
      <strong>(e) Financial Responsibility.</strong> The credit or refund amount
      (less any non-refundable fees) will be deducted from Promoter's payout or
      charged to Promoter's payment method on file.
    </p>
    <p className="indent">
      <strong>(f) Marketing.</strong> Pit Pay may market Buyer Protection
      programs at checkout and in promotional materials when Promoter has opted
      in.
    </p>
    <p className="indent">
      <strong>(g) Program Terms.</strong> Pit Pay may establish terms,
      conditions, and deadlines for Buyer Protection requests. Pit Pay may
      modify program terms upon notice.
    </p>
    <p className="indent">
      <strong>(h) Timely Resolution.</strong> Promoter must resolve Buyer
      Protection requests in a timely manner. Failure may result in payout
      withholding or Pit Pay processing requests on Promoter's behalf.
    </p>

    <h3>8. REGULATORY COMPLIANCE</h3>
    <h4>8.1 Fee Display and Pricing</h4>
    <p className="indent">
      <strong>(a) Compliance Obligation.</strong> Promoter is solely responsible
      for complying with all fee display and pricing laws in its jurisdiction.
    </p>
    <p className="indent">
      <strong>(b) Platform Options.</strong> The Platform provides all-in
      pricing and itemized pricing options. Promoter must select the method that
      complies with applicable law.
    </p>
    <p className="indent">
      <strong>(c) Mandatory All-In Pricing.</strong> Pit Pay may require all-in
      pricing based on jurisdiction or regulatory requirements.
    </p>
    <h4>8.2 General Compliance</h4>
    <p>
      Promoter shall comply with all applicable laws governing live event
      ticketing, consumer protection, refund disclosures, and gift card/stored
      value regulations.
    </p>

    <h3>9. DATA AND INTELLECTUAL PROPERTY</h3>
    <h4>9.1 Data Ownership</h4>
    <p>
      Promoter retains ownership of all Promoter Content and Transaction Data.
    </p>
    <h4>9.2 License to Pit Pay</h4>
    <p>
      Promoter grants Pit Pay a non-exclusive, royalty-free license to use
      Promoter Content and Transaction Data as necessary to provide the
      Platform.
    </p>
    <h4>9.3 Aggregated Data</h4>
    <p>
      Pit Pay may aggregate and anonymize data for product improvement,
      benchmarking, and industry reporting.
    </p>
    <h4>9.4 Privacy Compliance</h4>
    <p>
      Each party shall comply with applicable data protection and privacy laws,
      including CCPA.
    </p>
    <h4>9.5 Data Security</h4>
    <p>
      Pit Pay shall maintain commercially reasonable safeguards to protect
      Transaction Data.
    </p>
    <h4>9.6 Data Breach Notification</h4>
    <p>
      Pit Pay shall notify Promoter within seventy-two (72) hours of discovering
      a security breach affecting Transaction Data.
    </p>
    <h4>9.7 Marks</h4>
    <p>
      Promoter grants Pit Pay a royalty-free license to use Promoter's marks for
      marketing. Pit Pay grants Promoter a license to use Ticket Hoss and Pit
      Pay marks for Platform-related marketing.
    </p>

    <h3>10. MESSAGING AND COMMUNICATIONS</h3>
    <h4>10.1 Messaging Services</h4>
    <p>
      The Platform provides SMS, email, and push notification capabilities ("
      <strong>Messaging Services</strong>").
    </p>
    <h4>10.2 Messaging Allocation</h4>
    <p>
      Promoter receives up to 10,000 SMS messages per calendar year at no
      charge. Additional messages are charged at established rates. Push
      notifications are unlimited.
    </p>
    <h4>10.3 Compliance</h4>
    <p>
      Promoter is solely responsible for complying with TCPA, CAN-SPAM, and all
      other applicable messaging laws.
    </p>
    <h4>10.4 Messaging Indemnification</h4>
    <p>
      Promoter shall indemnify Pit Pay from all claims arising from Promoter's
      use of Messaging Services.
    </p>

    <h3>11. TERM AND TERMINATION</h3>
    <h4>11.1 Term</h4>
    <p>
      These Terms commence upon Promoter's acceptance and continue until
      terminated.
    </p>
    <h4>11.2 Termination for Convenience</h4>
    <p>Either party may terminate upon ninety (90) days' written notice.</p>
    <h4>11.3 Termination for Cause</h4>
    <p>
      Either party may terminate immediately if the other materially breaches
      and fails to cure within fifteen (15) days.
    </p>
    <h4>11.4 Effect of Termination</h4>
    <p className="indent">
      <strong>(a) Pending Events.</strong> Pit Pay will process transactions for
      Events within thirty (30) days of termination.
    </p>
    <p className="indent">
      <strong>(b) Outstanding Amounts.</strong> Termination does not relieve
      payment obligations.
    </p>
    <p className="indent">
      <strong>(c) Credits.</strong> Promoter remains responsible for honoring
      outstanding credits.
    </p>

    <h3>12. LIABILITY AND INDEMNIFICATION</h3>
    <h4>12.1 Promoter Indemnification</h4>
    <p>
      Promoter shall indemnify Pit Pay from all claims arising from: personal
      injury, property damage, or death at Events; Promoter's acts or omissions;
      breach of these Terms; and violation of applicable law.
    </p>
    <h4>12.2 Limitation of Liability</h4>
    <p>
      <strong>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, PIT PAY'S AGGREGATE LIABILITY
        SHALL NOT EXCEED THE TOTAL FEES RECEIVED FROM PROMOTER IN THE NINETY
        (90) DAYS PRECEDING THE CLAIM. PIT PAY SHALL NOT BE LIABLE FOR ANY
        INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
      </strong>
    </p>
    <h4>12.3 Disclaimer of Warranties</h4>
    <p>
      <strong>
        THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS
        OR IMPLIED.
      </strong>
    </p>

    <h3>13. GENERAL PROVISIONS</h3>
    <h4>13.1 Governing Law</h4>
    <p>
      These Terms are governed by North Carolina law. Disputes shall be resolved
      in Mecklenburg County, North Carolina.
    </p>
    <h4>13.2 Assignment</h4>
    <p>
      Promoter may not assign these Terms without Pit Pay's consent. Pit Pay may
      assign in connection with a merger or acquisition.
    </p>
    <h4>13.3 Entire Agreement</h4>
    <p>
      These Terms, together with all exhibits, constitute the entire agreement.
      Pit Pay may update the Fee Schedule upon thirty (30) days' notice.
    </p>
    <h4>13.4 Severability</h4>
    <p>
      If any provision is unenforceable, it shall be modified or severed without
      affecting other provisions.
    </p>
    <h4>13.5 Electronic Acceptance</h4>
    <p>
      By using the Platform, Promoter agrees to these Terms. Electronic
      acceptance is legally binding.
    </p>

    <h3 className="exhibit-title">EXHIBIT A: FEE SCHEDULE</h3>
    <p>
      The following fees apply to sales processed through the Ticket Hoss
      Platform. Fees are subject to change upon thirty (30) days' written
      notice.
    </p>

    <h4>1. Fee Components</h4>
    <table>
      <thead>
        <tr>
          <th>Component</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Platform Fee</td>
          <td data-label="Description">Ticket Hoss fee for Platform access and services</td>
        </tr>
        <tr>
          <td>Processing Fee</td>
          <td data-label="Description">
            3.25% (all-in pricing) or 4.25% (participant-paid, includes 1% Cash
            Back)
          </td>
        </tr>
        <tr>
          <td>Transaction Fee</td>
          <td data-label="Description">$0.30 per online transaction; $0.10 per POS transaction</td>
        </tr>
      </tbody>
    </table>

    <h4>2. Online Sales - All-In Pricing</h4>
    <p>
      Fees are included in the displayed price and deducted from Promoter's
      payout.
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>General Admission Tickets</td>
          <td data-label="Platform Fee">$1.25</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Tickets Priced &le;$5</td>
          <td data-label="Platform Fee">$0.75</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Free Tickets</td>
          <td data-label="Platform Fee">Free</td>
          <td data-label="Processing">Free</td>
          <td data-label="Transaction">Free</td>
        </tr>
        <tr>
          <td>Reserved Seats</td>
          <td data-label="Platform Fee">$1.75</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Other Items (merch, add-ons)</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Reserved Items (camping, map-based)</td>
          <td data-label="Platform Fee">Custom</td>
          <td data-label="Processing">Custom</td>
          <td data-label="Transaction">Custom</td>
        </tr>
      </tbody>
    </table>

    <h4>3. Online Sales - Participant-Paid Pricing</h4>
    <p>
      Fees are charged to the Ticket Holder in addition to the ticket price.
      Processing fee includes 1% Cash Back.
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>General Admission Tickets</td>
          <td data-label="Platform Fee">$1.25</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Tickets Priced &le;$5</td>
          <td data-label="Platform Fee">$0.75</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Free Tickets</td>
          <td data-label="Platform Fee">Free</td>
          <td data-label="Processing">Free</td>
          <td data-label="Transaction">Free</td>
        </tr>
        <tr>
          <td>Reserved Seats</td>
          <td data-label="Platform Fee">$1.75</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Other Items (merch, add-ons)</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">4.25%</td>
          <td data-label="Transaction">$0.30</td>
        </tr>
        <tr>
          <td>Reserved Items (camping, map-based)</td>
          <td data-label="Platform Fee">Custom</td>
          <td data-label="Processing">Custom</td>
          <td data-label="Transaction">Custom</td>
        </tr>
      </tbody>
    </table>

    <h4>4. Walk-Up POS Sales</h4>
    <p>
      All POS sales use all-in pricing. Fees are deducted from Promoter's
      payout.
    </p>
    <p>
      <strong>
        <em>Credit Card Transactions</em>
      </strong>
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>General Admission Tickets</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.10</td>
        </tr>
        <tr>
          <td>Reserved Seats</td>
          <td data-label="Platform Fee">$1.25</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.10</td>
        </tr>
        <tr>
          <td>Other Items (merch, add-ons)</td>
          <td data-label="Platform Fee">2%</td>
          <td data-label="Processing">3.25%</td>
          <td data-label="Transaction">$0.10</td>
        </tr>
      </tbody>
    </table>
    <p>
      <strong>
        <em>Cash Transactions</em>
      </strong>
    </p>
    <table>
      <thead>
        <tr>
          <th>Product Type</th>
          <th>Platform Fee</th>
          <th>Processing</th>
          <th>Transaction</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>General Admission Tickets</td>
          <td data-label="Platform Fee">Free</td>
          <td data-label="Processing">N/A</td>
          <td data-label="Transaction">N/A</td>
        </tr>
        <tr>
          <td>Reserved Seats</td>
          <td data-label="Platform Fee">$1.25</td>
          <td data-label="Processing">N/A</td>
          <td data-label="Transaction">N/A</td>
        </tr>
        <tr>
          <td>Other Items (merch, add-ons)</td>
          <td data-label="Platform Fee">Free</td>
          <td data-label="Processing">N/A</td>
          <td data-label="Transaction">N/A</td>
        </tr>
      </tbody>
    </table>

    <h4>5. Additional Fees</h4>
    <table>
      <thead>
        <tr>
          <th>Fee Type</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>International Cards</td>
          <td data-label="Amount">+1.5% additional processing fee</td>
        </tr>
        <tr>
          <td>Chargeback Fee</td>
          <td data-label="Amount">$15 per dispute (subject to change by Payment Processor)</td>
        </tr>
        <tr>
          <td>Reserved Items</td>
          <td data-label="Amount">Custom pricing (camping, map-based inventory)</td>
        </tr>
      </tbody>
    </table>

    <h4>6. Important Notes</h4>
    <ul>
      <li>All fees are non-refundable regardless of who pays them.</li>
      <li>
        The 1% Cash Back (included in Participant-Paid processing fee) is paid
        to Promoter annually.
      </li>
      <li>
        Tiered pricing for the same seat does not affect the fee structure; fees
        are based on ticket type.
      </li>
      <li>
        Reserved Items include camping spots and any map-based inventory other
        than reserved seats.
      </li>
      <li>
        Pit Pay may modify this Fee Schedule upon thirty (30) days' written
        notice.
      </li>
    </ul>
  </>
);

export default TicketHossTermsContent;
